import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import { useApi } from '@backstage/core-plugin-api';
import { TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { useEffect, useState } from 'react';

const knownAnnotations = {
  email: 'microsoft.com/email',
  userId: 'graph.microsoft.com/user-id',
};

export interface UserPickerProps {
  placeholder?: string;
  label: string;
  value?: UserInfo;
  onValueChange: (newUser: UserInfo | null) => void;
}

export interface UserInfo {
  name: string;
  displayName: string;
  entityRef: string;
  email: string;
  userId: string;
}

function getUserDisplayName(entity: Entity): string {
  const profile = (entity?.spec?.profile as Record<string, string>) ?? {};
  if (entity.kind === 'User' && profile.displayName) {
    return profile.displayName;
  }
  return entity.metadata.name;
}

function getAnnotation(entity: Entity, annotation: string) {
  return entity.metadata?.annotations?.[annotation] ?? '';
}

function userFromEntity(entity: Entity | null): UserInfo | null {
  if (!entity || entity.kind !== 'User') {
    return null;
  }

  const entityRef = stringifyEntityRef(entity);
  const name = entity.metadata.name;
  const displayName = getUserDisplayName(entity);
  const email = getAnnotation(entity, knownAnnotations.email);
  const userId = getAnnotation(entity, knownAnnotations.userId);

  return {
    name,
    email,
    userId,
    displayName,
    entityRef,
  };
}

function useUserEntities() {
  const catalogApi = useApi(catalogApiRef);
  const {
    value: entities,
    loading,
    error,
  } = useAsync(async () => {
    const response = await catalogApi.getEntities({
      filter: {
        kind: 'User',
      },
    });

    return (
      response.items?.map(it => ({
        ...it,
        id: stringifyEntityRef(it),
      })) ?? []
    );
  });

  return {
    loading,
    error,
    entities,
  };
}

export const UserPicker = (props: UserPickerProps) => {
  const { onValueChange, value, label, placeholder } = props;
  const { loading, error, entities } = useUserEntities();
  const [initial, setInitial] = useState<UserInfo | undefined>(value);

  const filteredEntities = entities ?? [];
  const selectedEntity =
    filteredEntities.find(
      it => value && stringifyEntityRef(it) === value.entityRef,
    ) ?? null;

  useEffect(() => {
    if (selectedEntity && initial === value) {
      onValueChange(userFromEntity(selectedEntity));
      setInitial(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error occurred: {error.message}</div>;

  return (
    <Autocomplete
      fullWidth
      value={selectedEntity}
      getOptionLabel={getUserDisplayName}
      getOptionSelected={(left, right) =>
        stringifyEntityRef(left) === stringifyEntityRef(right)
      }
      onChange={(_, newValue) => onValueChange(userFromEntity(newValue))}
      options={filteredEntities}
      renderInput={params => (
        <TextField
          {...params}
          label={label || 'User'}
          placeholder={placeholder}
          fullWidth
        />
      )}
    />
  );
};
